import { html, type Html, Page, View } from 'rune-ts';
import klass from './SearchResult.module.scss';
import { LayoutView } from '../../components/cells/Layout/LayoutView';
import { footer_links } from '../../components/cells/Footer/constant';
import { SEARCH_TAB_MAP } from '../../../features/Search/const';
import { ProductCardList } from '../../components/cells/ProductCardList/ProductCardList';
import { staticTypo, staticTypoPcMo, typo, typoPcMo } from '../../../shared/typography/typo';
import { Tabs } from '../../components/cells/Tabs/Tabs';
import { htmlIf, updateUrlParams } from '../../../shared/util';
import { ProductListTypeS } from '../../../features/ProductList/outbound/share';
import { CurationListItemView } from '../../components/cells/CurationListItem/CurationListItem';
import { Pagination, PaginationChangeEvent } from '../../components/cells/Pagination/Pagination';
import { CurationListItem } from '../../../features/Curation/type';
import { Dropdown, DropdownChangeEvent } from '../../components/cells/Dropdown/Dropdown';
import { PRODUCT_LIST_SORT } from '../../../features/ProductList/constant';
import {
  PopupStoreItemCard,
  PopupStoreItemCardProps,
} from '../../components/cells/PopupStore/PopupStoreItemCard';
import { SearchEmptyScreen } from './views/SearchEmptyScreen';
import { BaseLayoutOptions } from '../../../features/Core/service';
import { PlusShopCard } from '../../components/cells/PlusShopCard/PlusShopCard';
import { PlusShopCardType } from '../../../features/PlusShop/type';
import { generateSearchUrl } from '../../../features/Search/const/url';
import { ProductCardSize } from '@services/MarppleShop/features/ProductList/type';
import { font } from '@services/MarppleShop/shared/typography';
import { CreatorSearchData } from '@services/MarppleShop/features/Creator/type';
import { CreatorSearchCard } from '@services/MarppleShop/renderApp/components/cells/CreatorSearchCard/CreatorSearchCard.ts';
import { SearchPageController } from '@services/MarppleShop/renderApp/pages/SearchResult/controllers/SearchResult.controller.ts';

export interface SearchResultData {
  products?: ProductListTypeS.ProductCardData[];
  plus_shops?: PlusShopCardType[];
  creators?: CreatorSearchData[];
  popup_stores?: PopupStoreItemCardProps['item'][];
  curations?: CurationListItem[];
  recommended_products?: ProductListTypeS.ProductCardData[];
  adult_check_needed: boolean;
}

export type SearchCountData = Record<keyof typeof SEARCH_TAB_MAP, number>;

export type SearchResultPageData = SearchResultData & {
  keyword: string;
  count: SearchCountData;
  current_tab: keyof typeof SEARCH_TAB_MAP;
  current_page?: number;
  sort?: keyof typeof PRODUCT_LIST_SORT;
  layout_options: BaseLayoutOptions;
  is_mobile: boolean;
  tab_count: number;
};

class SeeMore extends View<{ url: string; is_mobile: boolean }> {
  override template() {
    return html`
      <a href="${this.data.url}" class="${klass.see_more} ${font('16_medium', '12_medium')}">
        ${ET('mps2::gnb::more')}
      </a>
    `;
  }
}

class SearchSectionTitle extends View<{
  label: string;
  key: string;
  count: number;
  is_mobile: boolean;
  postfix?: View;
}> {
  override template() {
    const { label, count, postfix, is_mobile } = this.data;

    return html`
      <div
        class="${klass.section_title} ${typoPcMo({ is_mobile, pc: '24_bold', mo: '16_bold' })}"
        data-count="${count}"
      >
        <span
          >${label}<span
            class="${klass.count} ${staticTypoPcMo({ is_mobile, pc: 'unica_24_bold', mo: 'unica_16_bold' })}"
            >${count.toLocaleString()}</span
          ></span
        >
        ${postfix ?? ''}
      </div>
    `;
  }
}

interface ProductSortOption {
  key: keyof typeof PRODUCT_LIST_SORT;
  value: keyof typeof PRODUCT_LIST_SORT;
  name: string;
}

const product_sort_options: ProductSortOption[] = [
  {
    key: PRODUCT_LIST_SORT.popular,
    value: PRODUCT_LIST_SORT.popular,
    name: ET('mps2::product::sort::popular'),
  },
  {
    key: PRODUCT_LIST_SORT.newest,
    value: PRODUCT_LIST_SORT.newest,
    name: ET('mps2::product::sort::newest'),
  },
  {
    key: PRODUCT_LIST_SORT.price_low_to_high,
    value: PRODUCT_LIST_SORT.price_low_to_high,
    name: ET('mps2::product::sort::price_low_to_high'),
  },
  {
    key: PRODUCT_LIST_SORT.price_high_to_low,
    value: PRODUCT_LIST_SORT.price_high_to_low,
    name: ET('mps2::product::sort::price_high_to_low'),
  },
  {
    key: PRODUCT_LIST_SORT.many_reviews,
    value: PRODUCT_LIST_SORT.many_reviews,
    name: ET('mps2::product::sort::many_reviews'),
  },
];

class SearchTitle extends View<Pick<SearchResultPageData, 'count' | 'keyword'>> {
  override template() {
    const { count, keyword } = this.data;
    const is_empty_result = count.all === 0;

    // en
    if (ET.lang === 'en') {
      if (is_empty_result) {
        // 영문일 때 i18n 키 맵핑이 쉽지 않아서 하드코딩함
        return html`<span>
          result for
          <span class="${typo('32_bold')} ${klass.pointed}">${html.preventEscape(keyword)}</span> search
        </span>`;
      }
      return html`<span>
        <span class="${typo('32_bold')} ${klass.pointed}">${count.all.toLocaleString()}</span>
        result${count.all > 1 ? 's' : ''} for
        <span class="${typo('32_bold')} ${klass.pointed}">${html.preventEscape(keyword)}</span> search
      </span>`;
    }

    // kr, jp
    const keyword에_대한 = html`<span class="${typo('32_bold')} ${klass.pointed}"
        >${html.preventEscape(keyword)}</span
      >${ET('mps2::search::title1')}`;
    const n개의 = html`<span class="${staticTypo('unica_32_bold')} ${klass.pointed}"
        >${count.all.toLocaleString()}</span
      ><span class="${typo('32_bold')} ${klass.pointed}">${ET('mps2::search::title2')}</span>${ET(
        'mps2::search::title3',
      )}`;
    const 검색_결과 = ET('mps2::search::title4');

    if (is_empty_result) {
      return html`${keyword에_대한} ${검색_결과}`;
    }
    return html`${keyword에_대한} ${n개의} ${검색_결과}`;
  }
}

class SearchResultPageBodyView extends View<SearchResultPageData> {
  private controller = new SearchPageController(this.data.layout_options);

  override template() {
    const {
      keyword,
      products = [],
      plus_shops = [],
      creators = [],
      popup_stores = [],
      curations = [],
      recommended_products = [],
      count,
      current_tab,
      adult_check_needed,
      is_mobile,
      tab_count,
    } = this.data;
    const is_all_tab = current_tab === SEARCH_TAB_MAP.all.key;

    return html`<div class="${klass.search_page}" data-current_tab="${current_tab}">
      <div class="${typo('32_medium')} ${klass.title_container}">${new SearchTitle({ count, keyword })}</div>

      ${htmlIf(new SearchEmptyScreen(), this.isResultEmpty())}
      ${htmlIf(
        new Tabs({
          size: 'small',
          current_tab_id: current_tab,
          tabs: Object.values(SEARCH_TAB_MAP).map((tab) => ({
            href: tab.query
              ? generateSearchUrl(keyword, tab.query as keyof typeof SEARCH_TAB_MAP)
              : generateSearchUrl(keyword),
            label: tab.label,
            id: tab.key,
            data: { count: count[tab.key] },
            klass: count[tab.key] < 1 ? klass.hidden : undefined,
          })),
          custom_klass: klass.tab_container,
        }),
        /* 결과값이 있고, all 빼고 하나이상이면 탭 추가 */
        !this.isResultEmpty() && tab_count > 1,
      )}

      <div class="${klass.product_section} ${htmlIf(klass.hidden, !products?.length)}">
        ${new SearchSectionTitle({
          ...SEARCH_TAB_MAP.product,
          is_mobile,
          count: count.product,
          postfix: is_all_tab
            ? new SeeMore({
                is_mobile,
                url: `${SEARCH_TAB_MAP.product.url}&keyword=${keyword}`,
              })
            : new Dropdown(
                {
                  options: product_sort_options,
                  selected_option_key: this.data.sort,
                },
                {
                  horizontal: 'right',
                  without_container: is_mobile,
                  fit_width_to_longest_name: true,
                  text_align: is_mobile ? 'right' : 'left',
                },
              ),
        })}
        ${new ProductCardList(
          { products },
          {
            resize_width: 600,
            is_lazy: true,
            container_klass: klass.product_cards_container,
            klass: klass.product_card,
            hide_badges: is_mobile && is_all_tab,
            hide_tag: is_mobile && is_all_tab,
            size: ProductCardSize.medium,
          },
        )}
      </div>

      <div class="${klass.plus_shop_section} ${htmlIf(klass.hidden, !plus_shops.length)}">
        ${new SearchSectionTitle({
          ...SEARCH_TAB_MAP.plus_shop,
          is_mobile,
          count: count.plus_shop,
          postfix: is_all_tab
            ? new SeeMore({ is_mobile, url: `${SEARCH_TAB_MAP.plus_shop.url}&keyword=${keyword}` })
            : undefined,
        })}
        <div class="${klass.plus_shops_container}">
          ${plus_shops.map((plus_shop) => new PlusShopCard(plus_shop, { adult_check_needed }))}
        </div>
      </div>

      <div class="${klass.creator_section} ${htmlIf(klass.hidden, !creators.length)}">
        ${new SearchSectionTitle({
          ...SEARCH_TAB_MAP.creator,
          is_mobile,
          count: count.creator,
          postfix: is_all_tab
            ? new SeeMore({ is_mobile, url: `${SEARCH_TAB_MAP.creator.url}&keyword=${keyword}` })
            : undefined,
        })}
        <div class="${klass.creators_container}">
          ${creators.map((creator, index) => {
            return new CreatorSearchCard({
              creator,
              is_mobile,
              is_lazy: is_mobile && index > 5,
            });
          })}
        </div>
      </div>

      <div class="${klass.popup_store_section} ${htmlIf(klass.hidden, !popup_stores.length)}">
        ${new SearchSectionTitle({
          ...SEARCH_TAB_MAP.popup_store,
          is_mobile,
          count: count.popup_store,
          postfix: is_all_tab
            ? new SeeMore({ is_mobile, url: `${SEARCH_TAB_MAP.popup_store.url}&keyword=${keyword}` })
            : undefined,
        })}
        <div class="${klass.popup_stores_container}">
          ${popup_stores.map(
            (popup_store) =>
              new PopupStoreItemCard({
                item: popup_store,
                use_in: 'search',
                is_mobile,
              }),
          )}
        </div>
      </div>

      <div class="${klass.curation_section} ${htmlIf(klass.hidden, !curations.length)}">
        ${new SearchSectionTitle({
          ...SEARCH_TAB_MAP.curation,
          is_mobile,
          count: count.curation,
          postfix: is_all_tab
            ? new SeeMore({ is_mobile, url: `${SEARCH_TAB_MAP.curation.url}&keyword=${keyword}` })
            : undefined,
        })}
        <div class="${klass.curations_container}">
          ${curations.map(
            (curation) =>
              new CurationListItemView(
                { curation },
                {
                  klass: klass.curation,
                  is_mobile,
                  thumbnail_type: is_mobile ? 'short' : 'long',
                  without_products: is_mobile,
                  without_tags: is_all_tab,
                },
              ),
          )}
        </div>
      </div>

      ${htmlIf(
        html`<div class="${klass.recommend_section}">
          <span class="${typoPcMo({ pc: '16_bold', mo: '12_medium', is_mobile })} ${klass.section_title}">
            ${ET('mps2::product::recommend')}
          </span>
          ${new ProductCardList(
            { products: recommended_products },
            {
              size: ProductCardSize.medium,
              resize_width: is_mobile ? 400 : 600,
              is_lazy: true,
              container_klass: is_mobile
                ? klass.recommended_products_container
                : klass.product_cards_container,
              klass: klass.product_card,
            },
          )}
        </div>`,
        !!recommended_products.length && this.isResultEmpty(),
      )}
      ${!is_all_tab
        ? new Pagination(
            {
              page: this.data.current_page ?? 1,
              size: is_mobile ? 5 : 10,
              total: Pagination.calcTotal({
                total_count: this.data.count[this.data.current_tab],
                limit_per_page: 100,
              }),
            },
            { klass: klass.pagination },
          )
        : ''}
      ${this.controller.collaborationModalView}
    </div>`;
  }

  override onMount() {
    history.replaceState(
      null,
      '',
      updateUrlParams(location.href, {
        tab: SEARCH_TAB_MAP[this.data.current_tab].query,
      }),
    );
  }

  override onRender() {
    this.delegate(PaginationChangeEvent, Pagination, (e, view) => {
      const { page } = e.detail;

      location.replace(updateUrlParams(location.href, { page }));
      view.disableClick();
    });

    // product tab only
    if (this.data.current_tab === SEARCH_TAB_MAP.product.key) {
      this.delegate(
        DropdownChangeEvent<keyof typeof PRODUCT_LIST_SORT>,
        Dropdown<keyof typeof PRODUCT_LIST_SORT>,
        async (e) => {
          const sort = e.detail.option?.value;
          if (!sort) {
            throw new TypeError(`sort is required.`);
          }
          location.replace(updateUrlParams(location.href, { sort, page: 1 }));
        },
      );
    }

    this.controller.delegateProposalCollaboration(this);

    this.controller.delegateFanLetterButtonClick(this);
  }

  private isResultEmpty(): boolean {
    return this.data.count.all < 1;
  }
}

export class SearchResultPage extends Page<SearchResultPageData> {
  protected override template(): Html {
    const { layout_options } = this.data;

    return html`
      <div">
        ${new LayoutView(
          {
            body: new SearchResultPageBodyView(this.data),
            footer_data: { ...footer_links, is_mobile: layout_options.is_mobile, lang: ET.lang },
          },
          {
            full_width: layout_options.is_mobile,
            ...layout_options,
          },
        )}
      </div>
    `;
  }
}
